import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormButton, FormSelect, FormTextInput } from 'thermia-react-container';
import { countryArray } from '../../../../../translations/locales/countryArray';
import { RegistrationDTO } from '../../../../../types/registrationTypes';
import RegistrationsCustomerYup from '../../../../../validation/yup-input/registrationCustomer';
import CompleteRegistrationButton from '../../../components/completeRegistrationButton';
import { StyledContainer } from '../../../registration';
import InsuranceSelector from '../components/insuranceSelector';
import {useRegistrationConfigHook} from "../../../hooks/useRegistrationConfigHook";

interface Props {
  customer?: RegistrationDTO['customer'];
  defaultCountry: string;
  updateCustomer: (data: RegistrationDTO['customer']) => Promise<void>;
  loading: boolean;
  preventSwitchTabDueToUnsavedData: (value: boolean) => void;
  disabled: boolean;
}

type CountryForSelect = {
  label: string;
  value: string;
  name: string;
  sortPriority: number;
};

const sortByLabel = (a: CountryForSelect, b: CountryForSelect) => a.sortPriority - b.sortPriority || a.name.localeCompare(b.name, 'sv');

const CustomerForm: FC<Props> = ({ customer, defaultCountry, updateCustomer, loading, preventSwitchTabDueToUnsavedData, disabled }) => {
  const { 
    validCustomerTypes, 
    registrationDefaultValues,
    getCustomerTypeConfigurationByCode, 
    registrationDefaultValuesByCountry, 
    isPrivateCustomerType
  } = useRegistrationConfigHook();
  const [ selectedCustomerType, setSelectedCustomerType ] = useState<string>(customer?.type ?? validCustomerTypes[0]?.type ?? '');
  const [ isPrivate , setIsPrivate ] = useState<boolean>(isPrivateCustomerType(selectedCustomerType));

  const { t } = useTranslation('registrations');

  const schema = RegistrationsCustomerYup(isPrivate);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      address: customer?.address ?? '',
      zipCode: customer?.zipCode ?? '',
      city: customer?.city ?? '',
      phone: customer?.phone ?? registrationDefaultValues?.phoneCountryCode ?? '',
      email: customer?.email ?? '',
      type: customer?.type ?? registrationDefaultValues?.customerCode ?? '',
      insuranceType: customer?.insuranceType ?? registrationDefaultValues?.insuranceType ?? '',
      country: customer?.country ?? defaultCountry
    }
  });

  const onValid = async (data: RegistrationDTO['customer'] | undefined) => {
    try {
      await updateCustomer(data as RegistrationDTO['customer']);
      reset(data);
    } catch (error) {}
  };

  const handleOnChangeCustomerType = useCallback((selectedCustomerCode: string) => {
    setSelectedCustomerType(selectedCustomerCode);
    setIsPrivate(isPrivateCustomerType(selectedCustomerCode));
  }, [setSelectedCustomerType, setIsPrivate, isPrivateCustomerType]);

  const getCustomerFirstNameLabel = useCallback(() => {
    if (isPrivate) {
      return t('registrations.general.firstName');
    }
    const customerType = getCustomerTypeConfigurationByCode(selectedCustomerType);
    return customerType
    ? t('registrations.insuranceDescriptions.' + customerType.displayName)
    : t('registrations.general.firstName');
  }, [isPrivate, t, selectedCustomerType, getCustomerTypeConfigurationByCode]);
  
  useEffect(() => {
    if (customer) {
      reset(customer);
    }
  }, [customer, reset]);

  useEffect(() => {
    if (preventSwitchTabDueToUnsavedData) {
      preventSwitchTabDueToUnsavedData(isDirty);
    }
  }, [preventSwitchTabDueToUnsavedData, isDirty]);

  const options = useMemo(
    () =>
      countryArray
        .map((country) => {
          let sortPriority;
          switch (country.countryCode) {
            case 'SE': {
              sortPriority = 1;
              break;
            }
            case 'NO': {
              sortPriority = 2;
              break;
            }
            case 'FI': {
              sortPriority = 3;
              break;
            }
            case 'DK': {
              sortPriority = 4;
              break;
            }
            default: {
              sortPriority = 99;
              break;
            }
          }
          return {
            label: `${country.flag} ${t(country.countryCode)}`,
            value: country.countryCode,
            name: country.countryCode,
            continent: country.continent,
            sortPriority
          };
        })
        .sort(sortByLabel),
    [t]
  );

  const handleOnCountryChange = (countryCode: string) => {
    const phone = getValues('phone');
    if (phone.length <= 5) {
      return setValue('phone', `${registrationDefaultValuesByCountry(countryCode)?.phoneCountryCode ?? ''}`);
    }
  };

  return (
    <>
      <StyledContainer>
        <Grid container>
          {/* Form inputs */}
          <Grid item xs={8} container spacing={2} rowSpacing={4}>
            <Grid item xs={12} container spacing={2} rowSpacing={4}>
              <InsuranceSelector disabled={disabled} control={control} onChangeCustomerType={handleOnChangeCustomerType} />
            </Grid>
            <Grid item xs={isPrivate ? 6 : 12}>
              <FormTextInput disabled={disabled} control={control} name="firstName" label={getCustomerFirstNameLabel()} />
            </Grid>
            {isPrivate && 
              <Grid item xs={6}>
                <FormTextInput disabled={disabled} control={control} name="lastName" label={t('registrations.general.lastName')} />
              </Grid>
            }
            <Grid item xs={12}>
              <FormTextInput disabled={disabled} control={control} name="address" label={t('registrations.general.address')} />
            </Grid>
            <Grid item xs={4}>
              <FormTextInput disabled={disabled} control={control} name="zipCode" label={t('registrations.general.zipCode')} />
            </Grid>
            <Grid item xs={8}>
              <FormTextInput disabled={disabled} control={control} name="city" label={t('registrations.general.city')} />
            </Grid>
            <Grid item xs={7}>
              <FormTextInput disabled={disabled} control={control} name="phone" label={t('registrations.general.phone')} />
            </Grid>
            <Grid item xs={5}>
              <FormSelect
                name={'country'}
                variant={'outlined'}
                label={t('registrations.general.selectCountry')}
                control={control}
                interceptOnChange={handleOnCountryChange}
                labelId="selectCountryCustomer-select-label"
                options={options}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={8}>
              <FormTextInput disabled={disabled} control={control} name="email" label={t('registrations.general.email')} />
            </Grid>
          </Grid>
          {/* Form submit */}
        </Grid>
      </StyledContainer>
      {!disabled && (
        <Grid container spacing={2} justifyContent={'end'} alignItems={'center'} marginTop={2}>
          <Grid item>
            <FormButton
              variant={'contained'}
              onClick={handleSubmit(onValid)}
              title={t('registrations.general.save')}
              loading={loading}
            />
          </Grid>
          <Grid item>
            <CompleteRegistrationButton formIsDirty={isDirty} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default CustomerForm;
